/**
 * API client for thread operations.
 *
 * Provides functions to interact with the backend thread storage API.
 */

import { Thread, Message } from '../types/Thread';
import { buildApiHeaders } from './apiUtils';

const API_BASE = '';  // Relative to the current origin (proxied by nginx)

/**
 * Fetch all threads for the current user.
 */
export async function fetchThreads(): Promise<Thread[]> {
    const headers = await buildApiHeaders();
    const response = await fetch(`${API_BASE}/api/threads`, {
        method: 'GET',
        headers,
        credentials: 'include',
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch threads: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Fetch a specific thread with all its messages.
 */
export async function fetchThread(threadId: string): Promise<Thread> {
    const headers = await buildApiHeaders();
    const response = await fetch(`${API_BASE}/api/threads/${threadId}`, {
        method: 'GET',
        headers,
        credentials: 'include',
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch thread: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Create a new thread.
 */
export async function createThread(
    id: string,
    title: string,
    corpusId?: string,
    paperTitle?: string
): Promise<Thread> {
    const headers = await buildApiHeaders();
    const response = await fetch(`${API_BASE}/api/threads`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
            id,
            title,
            ...(corpusId ? { corpusId } : {}),
            ...(paperTitle ? { paperTitle } : {}),
        }),
    });

    if (!response.ok) {
        throw new Error(`Failed to create thread: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Add a message to an existing thread.
 */
export async function addMessage(
    threadId: string,
    message: Message
): Promise<Message> {
    const headers = await buildApiHeaders();
    const response = await fetch(`${API_BASE}/api/threads/${threadId}/messages`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
            id: message.id,
            question: message.question,
            answer: message.answer,
            figures: message.figures,
        }),
    });

    if (!response.ok) {
        throw new Error(`Failed to add message: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Delete a thread and all its messages.
 */
export async function deleteThread(threadId: string): Promise<void> {
    const headers = await buildApiHeaders();
    const response = await fetch(`${API_BASE}/api/threads/${threadId}`, {
        method: 'DELETE',
        headers,
        credentials: 'include',
    });

    if (!response.ok) {
        throw new Error(`Failed to delete thread: ${response.statusText}`);
    }
}

/**
 * Update feedback for a specific message.
 */
export async function updateMessageFeedback(
    threadId: string,
    messageId: string,
    rating: 'good' | 'bad' | 'neutral',
    comment?: string
): Promise<Message> {
    const headers = await buildApiHeaders();
    const response = await fetch(`${API_BASE}/api/threads/${threadId}/messages/${messageId}/feedback`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({
            rating,
            ...(comment && { comment }),
        }),
    });

    if (!response.ok) {
        throw new Error(`Failed to update message feedback: ${response.statusText}`);
    }

    return response.json();
}
